import type { CalEvent, CalSource, Task } from "../api.ts";
import { addDays, captionStyle, colors, dayLabel, shortDate } from "../theme.ts";
import { GlassCard } from "./glass.tsx";
import { TaskRow } from "./TaskRow.tsx";

export type DayItem =
  | { kind: "task"; task: Task }
  | { kind: "event"; event: CalEvent; colour: string | undefined };

export interface Day { date: string; items: DayItem[] }

/** The date a task sits on: the date to chase while it waits, otherwise its own. */
export const dateOfTask = (t: Task) => (t.state === "waiting" ? t.follow_up_on : t.due);

/** The last day an event covers. An all-day event's end is the day after, as in iCal. */
const lastDay = (e: CalEvent) => (e.all_day && e.end_day > e.on_day ? addDays(e.end_day, -1) : e.end_day);

/**
 * Tasks and calendar events grouped by day, empty days left out. Within a day
 * the board's own items come first, then the feed's, all-day before timed. An
 * event that began before `from` sits on `from`.
 */
export function buildDays(tasks: Task[], events: CalEvent[], sources: CalSource[], from: string): Day[] {
  const byDay = new Map<string, DayItem[]>();
  const add = (date: string, item: DayItem) => { byDay.set(date, [...(byDay.get(date) ?? []), item]); };
  for (const task of tasks) {
    const d = dateOfTask(task);
    if (d) add(d, { kind: "task", task });
  }
  const colourOf = new Map(sources.map((s) => [s.id, s.colour]));
  for (const event of [...events].sort((a, b) => (a.on_day + (a.all_day ? "" : a.from_time)).localeCompare(b.on_day + (b.all_day ? "" : b.from_time)))) {
    add(event.on_day < from ? from : event.on_day, { kind: "event", event, colour: colourOf.get(event.source) });
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, items]) => ({
      date,
      items: [...items.filter((i) => i.kind === "task"), ...items.filter((i) => i.kind === "event")],
    }));
}

/** One card per day, in the same glass as Home's cards. Board items in ink, feed events lighter. */
export function DayList({
  days, today, onOpenTask, onOpenEvent,
}: { days: Day[]; today: string; onOpenTask: (id: string) => void; onOpenEvent?: (e: CalEvent) => void }) {
  return (
    <>
      {days.map((d) => (
        <GlassCard key={d.date} padding="14px 16px 2px" style={{ marginTop: 14 }}>
          <div style={captionStyle}>{d.date === today ? `Today · ${dayLabel(d.date)}` : dayLabel(d.date)}</div>
          {d.items.map((item) =>
            item.kind === "task" ? (
              <TaskRow
                key={item.task.id} task={item.task} onOpen={() => onOpenTask(item.task.id)}
                meta={[item.task.value_name, item.task.area_name].filter(Boolean).join(" › ")}
                quiet={item.task.state === "waiting" || item.task.blocked}
              />
            ) : (
              <EventRow key={item.event.id} event={item.event} colour={item.colour} onOpen={() => onOpenEvent?.(item.event)} />
            ),
          )}
        </GlassCard>
      ))}
    </>
  );
}

/** What an event says about when: the time, or "All day", and where it runs to if it covers more days. */
export function whenOf(e: CalEvent): string {
  const to = lastDay(e);
  const until = to > e.on_day ? `until ${shortDate(to)}` : "";
  if (e.all_day) return ["All day", until].filter(Boolean).join(", ");
  return [`${e.from_time} to ${e.to_time}`, until].filter(Boolean).join(", ");
}

/** The row grid of TaskRow, with the feed's colour as the rail and ink 2 for the title: lighter than the board's own. */
function EventRow({ event, colour, onOpen }: { event: CalEvent; colour: string | undefined; onOpen: () => void }) {
  return (
    <div
      onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      style={{
        display: "grid", gridTemplateColumns: "3px 1fr auto", gap: 12, alignItems: "center",
        padding: "12px 0", borderBottom: `1px solid ${colors.hair}`, cursor: "pointer",
      }}
    >
      <span style={{ width: 3, height: 22, borderRadius: 2, background: colour ?? colors.hair2, justifySelf: "center" }} />
      <div style={{ fontSize: 15, lineHeight: 1.3, color: colors.ink2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {event.summary ?? "Untitled"}
      </div>
      <div style={{ fontSize: 13, color: colors.ink2, fontVariantNumeric: "tabular-nums" }}>{event.all_day ? "" : event.from_time}</div>
    </div>
  );
}
